import { createDefaultSave, MAX_LIST, MAX_NAME, SAVE_VERSION, type SaveData } from './schema';

type Json = Record<string, unknown>;
type Migration = (data: Json, now: number) => Json;

const isObject = (v: unknown): v is Json =>
  typeof v === 'object' && v !== null && !Array.isArray(v);

/** migrations[n] upgrades a save from version n to n + 1. */
export const migrations: Record<number, Migration> = {
  0: (data, now) => {
    // Version 0 is the unversioned prototype shape: { name, muted }.
    const base = createDefaultSave(now) as unknown as Json;
    const profile = base.profile as Json;
    const settings = base.settings as Json;
    if (typeof data.name === 'string') profile.name = data.name;
    if (typeof data.muted === 'boolean') settings.muted = data.muted;
    return base;
  },
};

export class MigrationError extends Error {}

export function migrate(raw: unknown, now: number): SaveData {
  if (!isObject(raw)) throw new MigrationError('save is not an object');
  let data: Json = raw;
  let version = typeof data.version === 'number' ? data.version : 0;
  if (version > SAVE_VERSION) throw new MigrationError(`save version ${version} is newer than app`);
  while (version < SAVE_VERSION) {
    const step = migrations[version];
    if (!step) throw new MigrationError(`no migration from version ${version}`);
    data = { ...step(data, now), version: version + 1 };
    version++;
  }
  return sanitize(data, now);
}

const num = (v: unknown, fallback: number, min = 0, max = Number.MAX_SAFE_INTEGER): number =>
  typeof v === 'number' && Number.isFinite(v) ? Math.min(max, Math.max(min, v)) : fallback;
const bool = (v: unknown, fallback: boolean): boolean => (typeof v === 'boolean' ? v : fallback);
const str = (v: unknown, fallback: string, max = 10_000): string =>
  typeof v === 'string' ? v.slice(0, max) : fallback;
const list = <T>(v: unknown, keep: (item: Json) => T | null): T[] => {
  if (!Array.isArray(v)) return [];
  const out: T[] = [];
  for (const item of v) {
    if (!isObject(item)) continue;
    const kept = keep(item);
    if (kept) out.push(kept);
  }
  return out.slice(-MAX_LIST);
};

export function sanitize(data: Json, now: number): SaveData {
  const d = createDefaultSave(now);
  const profile = isObject(data.profile) ? data.profile : {};
  const settings = isObject(data.settings) ? data.settings : {};
  const stats = isObject(data.stats) ? data.stats : {};
  const quality = settings.quality;
  return {
    version: 1,
    profile: {
      name: str(profile.name, '', MAX_NAME),
      createdAt: num(profile.createdAt, now),
    },
    settings: {
      muted: bool(settings.muted, d.settings.muted),
      ambienceVolume: num(settings.ambienceVolume, d.settings.ambienceVolume, 0, 1),
      sfxVolume: num(settings.sfxVolume, d.settings.sfxVolume, 0, 1),
      vibration: bool(settings.vibration, d.settings.vibration),
      quality:
        quality === 'auto' || quality === 'low' || quality === 'mid' || quality === 'high'
          ? quality
          : 'auto',
      tutorialDone: bool(settings.tutorialDone, false),
    },
    stats: {
      visits: num(stats.visits, 0),
      lastVisitDate: str(stats.lastVisitDate, '', 10),
      incenseCount: num(stats.incenseCount, 0),
      bellCount: num(stats.bellCount, 0),
      chantCount: num(stats.chantCount, 0),
      bestTamAn: num(stats.bestTamAn, 0, 0, 100),
      bowCount: num(stats.bowCount, 0),
    },
    prayers: list(data.prayers, (p) =>
      typeof p.id === 'string' && typeof p.text === 'string'
        ? {
            id: p.id,
            text: p.text.slice(0, 120),
            kind: p.kind === 'lantern' ? 'lantern' : 'silk',
            createdAt: num(p.createdAt, now),
          }
        : null,
    ),
    fortunes: list(data.fortunes, (f) =>
      typeof f.id === 'string' && typeof f.fortuneId === 'number'
        ? {
            id: f.id,
            fortuneId: f.fortuneId,
            topic: str(f.topic, 'general', 32),
            confirmedByKeo: bool(f.confirmedByKeo, false),
            createdAt: num(f.createdAt, now),
          }
        : null,
    ),
    keoResults: list(data.keoResults, (k) =>
      typeof k.id === 'string' && (k.result === 'thanh' || k.result === 'tieu' || k.result === 'am')
        ? {
            id: k.id,
            ...(typeof k.question === 'string' ? { question: k.question.slice(0, 80) } : {}),
            result: k.result,
            createdAt: num(k.createdAt, now),
          }
        : null,
    ),
  };
}
